/**
 * checkInModel (BON-204 R2) — pure helpers behind the partial per-booking
 * check-in flow. Side-effect-free so the gate (AC5), the "Last, First" display +
 * last-then-first ordering with fully-in rows sunk to the bottom (revised AC4),
 * the search filter, the seats progress, and the party-of-1 vs stepper split are
 * unit-testable without React. Date math is UTC-anchored to
 * agree with the server: `checkInOpensAt` is the event's local-midnight as a UTC
 * instant, so formatting in UTC yields the event's local calendar date.
 */
import type { RosterBooking, WorkshopLifecycle } from '../../../../api/workshops';
import { isPublished } from '../workshop-lifecycle';

/** A booking is eligible for check-in iff it is ACTIVE (CONFIRMED, incl. comp). */
function isActive(b: RosterBooking): boolean {
  return b.status === 'CONFIRMED';
}

/** The ACTIVE (CONFIRMED, incl. comp) subset — the only rows the modal lists. */
export function activeCheckInBookings(bookings: RosterBooking[]): RosterBooking[] {
  return bookings.filter(isActive);
}

/** Only a published workshop takes check-ins — never a draft or a cancelled one. */
export function acceptsCheckIn(lifecycle: WorkshopLifecycle): boolean {
  return isPublished(lifecycle);
}

/** Open once `now >= checkInOpensAt` (the local-midnight UTC instant), forever. */
export function isCheckInOpen(checkInOpensAt: string, now: Date = new Date()): boolean {
  return now.getTime() >= new Date(checkInOpensAt).getTime();
}

/** The booking's whole party is present (and the party is non-empty). Drives the
 *  solid-green pill, the "sink to bottom" sort group, and the stepper max state. */
export function isFullyCheckedIn(b: RosterBooking): boolean {
  return b.attendeeCount > 0 && b.checkedInCount === b.attendeeCount;
}

/** A single-seat booking — tapped to toggle 0↔1 inline (no stepper). */
export function isPartyOfOne(b: RosterBooking): boolean {
  return b.attendeeCount === 1;
}

/** Split a "First [… Middle] Last" customer name into first/last parts. The final
 *  whitespace token is the last name; everything before it is the first/given
 *  part. A single token has no last name. */
function splitName(customerName: string): { first: string; last: string } {
  const parts = customerName.trim().split(/\s+/);
  if (parts.length <= 1) return { first: customerName.trim(), last: '' };
  const last = parts[parts.length - 1];
  return { first: parts.slice(0, -1).join(' '), last };
}

/** Display the customer as "Last, First" (revised AC4). Single-token names pass
 *  through unchanged (no comma). */
export function displayName(b: RosterBooking): string {
  const { first, last } = splitName(b.customerName);
  return last ? `${last}, ${first}` : first;
}

/** Compare by last name then first name (case-insensitive locale order). */
function byLastThenFirst(a: RosterBooking, b: RosterBooking): number {
  const na = splitName(a.customerName);
  const nb = splitName(b.customerName);
  return na.last.localeCompare(nb.last) || na.first.localeCompare(nb.first);
}

/**
 * Revised AC4 ordering: sort by last name then first name; FULLY checked-in
 * bookings (`checkedInCount === attendeeCount`) sink to the BOTTOM (sorted within
 * that group). Partials and empties stay in the top group. Pure — re-running
 * after a confirm re-sorts live. Does not mutate the input.
 */
export function sortForCheckIn(bookings: RosterBooking[]): RosterBooking[] {
  const top = bookings
    .filter((b) => !isFullyCheckedIn(b))
    .slice()
    .sort(byLastThenFirst);
  const bottom = bookings.filter(isFullyCheckedIn).slice().sort(byLastThenFirst);
  return [...top, ...bottom];
}

/** Seat-based check-in progress (AC3). Centralized so the modal's progress bar
 *  and the card subtext agree on numerator/denominator, "to go", and the capped
 *  percent. `total` is the active (CONFIRMED) seat count (`confirmedAttendees`);
 *  `checkedIn` is the Σ checked-in seat count (`checkedInSeats`). */
export interface CheckInProgress {
  checkedIn: number;
  total: number;
  toGo: number;
  /** Integer 0..100, capped; 0 when total is 0. Safe as a bar width. */
  percent: number;
}

export function checkInProgress(
  checkedInSeats: number,
  confirmedAttendees: number,
): CheckInProgress {
  const total = confirmedAttendees;
  const checkedIn = checkedInSeats;
  const toGo = Math.max(0, total - checkedIn);
  const percent = total > 0 ? Math.min(100, Math.round((checkedIn / total) * 100)) : 0;
  return { checkedIn, total, toGo, percent };
}

/** Case-insensitive substring match on customerName. Empty query → all. */
export function filterByName(bookings: RosterBooking[], query: string): RosterBooking[] {
  const q = query.trim().toLowerCase();
  if (q === '') return bookings;
  return bookings.filter((b) => b.customerName.toLowerCase().includes(q));
}
